import {
  AfterContentChecked,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  QueryList,
  ViewChildren,
} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {CmsComponent, EventService, Product, User, TranslatePipe} from '@spartacus/core';
import {CmsComponentData, CurrentProductService} from '@spartacus/storefront';
import {UserAccountFacade} from '@spartacus/user/account/root';
import {combineLatest, Observable, Subscription} from 'rxjs';
import {share} from 'rxjs/operators';
import {JsonService, LocalStorageService} from '@dcp/configurator';
import {DcpKymaService} from '@app/services/dcp-kyma.service';
import {LimitedHtmlComponent} from '@app/shared/limited-html/limited-html.component';
import {G2Product, G2Review, G2ReviewAnswers, G2ReviewList} from '../../models/g2-review.model';
import {DcpProductUtilitiesService} from '../../services/dcp-product-utilities.service';
import {NavigationLink} from '../navigation-bar/navigation-link';
import {ProductDetailsNavigationLinkService} from '../navigation-bar/product-details-navigation-link.service';
import {ReviewPreviewComponent} from './review-preview/review-preview.component';
import {WriteReviewDialogService} from './write-review/write-review-dialog.service';
import {EddlProductViewEvent} from '@app/services/analytics/EDDL/events/eddl-product-view-event';
import {EddlCollectionState} from '@app/services/analytics/EDDL/models/eddl-adobe-tracking-data';
import {FirstReviewComponent} from './first-review/first-review.component';
import {ReviewSummaryComponent} from './review-summary/review-summary.component';
import {DcpPaginationComponent} from '../../../shared/dcp-pagination/dcp-pagination.component';

export interface ReviewComponentData extends CmsComponent {
  classificationsProductData: string;
  filter: string;
}

export interface ReviewData {
  texts: ReviewTexts;
  answerOrder: string[];
}

export interface ReviewTexts {
  headline: string;
  sectionDescription?: string;
  infoText?: string;
  ctaText?: string;
  subSectionTitle?: string;
  subSectionDescription?: string;
}

@UntilDestroy()
@Component({
  selector: 'dcp-reviews',
  templateUrl: './review.component.html',
  styleUrls: ['./review.component.scss'],
  imports: [
    FirstReviewComponent,
    ReviewSummaryComponent,
    ReviewPreviewComponent,
    DcpPaginationComponent,
    TranslatePipe,
  ],
})
export class ReviewComponent implements OnInit, AfterContentChecked, OnDestroy {
  static REVIEWS_FOR_EXCLUSION: string = 'reviewsForExclusion';
  @ViewChildren(ReviewPreviewComponent) previews: QueryList<ReviewPreviewComponent>;
  limitedTextComponents: LimitedHtmlComponent[];
  currentPage: number = 0;
  navLink: NavigationLink = {
    headline: 'common.parameter',
    headlineParams: {parameter: ''},
    id: 'review-section',
  };
  product$: Observable<Product | null>;
  reviews$: Observable<G2ReviewList | null>;
  allReviews$: Observable<G2ReviewList | null>;
  componentData$: Observable<ReviewComponentData>;
  filters: string[];
  reviews: G2ReviewList = null;
  reviewTexts: ReviewTexts;
  reviewAnswerOrder: string[];
  defaultPageSize: number = 3;
  pageCount: number = 62;
  maxPageCount: number = 62;
  isListReady: boolean = false;
  productSubscription: Subscription = Subscription.EMPTY;
  reviewsSubscription: Subscription = Subscription.EMPTY;
  allReviewsSubscription: Subscription = Subscription.EMPTY;
  componentDataSubscription: Subscription = Subscription.EMPTY;
  routeSubscription: Subscription = Subscription.EMPTY;
  private currentProduct: Product;

  constructor(
    @Inject(PLATFORM_ID) public platformId: object,
    private currentProductService: CurrentProductService,
    private productDetailsNavigationLinkService: ProductDetailsNavigationLinkService,
    private cmsData: CmsComponentData<ReviewComponentData>,
    private changeDetector: ChangeDetectorRef,
    private reviewDialogService: WriteReviewDialogService,
    private localStorageService: LocalStorageService,
    private jsonService: JsonService,
    private kymaService: DcpKymaService,
    private productUtilitiesService: DcpProductUtilitiesService,
    private route: ActivatedRoute,
    private eventService: EventService,
    private userFacade: UserAccountFacade,
  ) {}

  ngOnInit(): void {
    this.product$ = this.currentProductService.getProduct().pipe(share());
    this.resetReviewsOnNavigation();
    this.loadComponentData();
    this.loadReviewData();
    this.triggerEddlProductView();
    this.localStorageService.removeItem(ReviewComponent.REVIEWS_FOR_EXCLUSION);
  }

  ngAfterContentChecked(): void {
    this.createPreviewArray(this.previews);
  }

  ngOnDestroy(): void {
    this.productSubscription.unsubscribe();
    this.reviewsSubscription.unsubscribe();
    this.componentDataSubscription.unsubscribe();
    this.routeSubscription.unsubscribe();
  }

  /**
   * Called when the user wants to show the next page of reviews.
   */
  pageChange(newPage: number) {
    this.currentPage = newPage;
    this.subscribeToReview(this.currentProduct, this.currentPage);
  }

  loadReviewData() {
    this.productSubscription = this.product$.pipe(untilDestroyed(this)).subscribe((p) => {
      this.currentProduct = p;
      this.getAllReviews(p, this.currentPage);
    });
  }

  /**
   * Fetches component data from cloud commerce.
   */
  loadComponentData() {
    this.componentData$ = this.cmsData.data$;
    this.componentDataSubscription = this.componentData$.pipe(untilDestroyed(this)).subscribe((d) => {
      this.filters = this.jsonService.parseSafely(d.filter) as string[];
      const reviewData: ReviewData = this.jsonService.parseSafely(d?.classificationsProductData) as ReviewData;
      if (reviewData.texts) {
        this.reviewTexts = reviewData.texts;
        this.reviewAnswerOrder = reviewData.answerOrder;
      } else {
        // The order was not originally planned to be part of this json.
        // This can be deleted when the patch_2020_08_DCP_2193 was executed on all systems.
        this.reviewTexts = reviewData as undefined as ReviewTexts;
      }
      this.navLink.headlineParams.parameter = this.reviewTexts?.headline;
    });
  }

  /**
   * Subscribes to the reviews returned by the kyma service.
   * Reviews Subscription determines the list for display.
   * Adds the Navigation link in the header navigation.
   *
   * @param currentProduct Product the reviews are for
   * @param currentPage Page number of the reviews.
   */
  subscribeToReview(currentProduct: Product, currentPage: number) {
    if (this.productUtilitiesService.getProductReviewsId(currentProduct)) {
      const exclusionList = this.jsonService.parseSafely(
        this.localStorageService.getItem(ReviewComponent.REVIEWS_FOR_EXCLUSION),
      );
      this.reviews$ = this.kymaService.getReviewsForProduct(
        currentProduct,
        currentPage + 1,
        this.defaultPageSize,
        exclusionList,
      );
      this.reviewsSubscription = this.reviews$.pipe(untilDestroyed(this)).subscribe((r) => {
        this.productDetailsNavigationLinkService.addNavigationLink(this.navLink);

        if (r.meta.page_count < this.maxPageCount) {
          this.pageCount = r.meta.page_count;
        } else if (exclusionList) {
          this.pageCount = Math.abs(this.maxPageCount - Math.ceil(exclusionList.length / this.defaultPageSize)) - 1;
        }
        this.kymaService.setReviewsData(r);
        this.reviews = r;

        if (this.pageCount <= 0) {
          const product: G2Product = r.meta.product;
          if (r.meta.record_count === 0) {
            product.star_rating = 0;
          }
          this.reviews = {meta: {page_count: 0, record_count: 0, product}} as G2ReviewList;
        }
        this.changeDetector.detectChanges();

        if (!this.isListReady) {
          this.isListReady = true;
          this.getAllReviews(currentProduct, currentPage + 1);
        }
      });
    }
  }

  /**
   * Defines a list of review's ids that should be excluded.
   * Combine the list of IDs for exclusion with the list of local storage IDs.
   *
   * @param reviews Product reviews that were received after the global API call.
   */
  setReviewsForExclusionId(reviews: G2ReviewList) {
    if (!reviews) {
      return;
    }
    let reviewsForExclusion: string[] = reviews.data.reduce((list: string[], review: G2Review) => {
      const isReviewContainTerm: boolean = this.filters.some((filter) =>
        this.isReviewContainsExclusionTerm(
          filter?.toLocaleLowerCase(),
          review.attributes.title,
          review.attributes.answers,
          review.attributes.user.name,
        ),
      );
      return isReviewContainTerm ? list.concat(review.id) : list;
    }, []);
    const localReviewsForExclusion = this.localStorageService.getItem(ReviewComponent.REVIEWS_FOR_EXCLUSION);
    if (localReviewsForExclusion) {
      reviewsForExclusion = [
        ...new Set([...reviewsForExclusion, ...this.jsonService.parseSafely(localReviewsForExclusion)]),
      ];
    }
    this.localStorageService.setItem(ReviewComponent.REVIEWS_FOR_EXCLUSION, JSON.stringify(reviewsForExclusion));
  }

  /**
   * Defines whether the current review contains a term that should be excluded.
   *
   * @param filter Verifiable term.
   * @param title Title of the current review.
   * @param answers Answers of the current review.
   * @param userName user name to filter.
   */
  isReviewContainsExclusionTerm(filter: string, title: string, answers: G2ReviewAnswers, userName: string): boolean {
    return (
      title?.toLocaleLowerCase()?.includes(filter) ||
      userName?.toLocaleLowerCase()?.includes(filter) ||
      Object.values(answers).some((answer) => answer.value?.toLocaleLowerCase()?.includes(filter))
    );
  }

  openRedirectToG2(g2Url: string) {
    this.reviewDialogService.prepareDialogTexts(g2Url, 'productReview.readAReview');
  }

  /**
   * Collects the LimitedHtmlComponents that are subcomponents of the collected ReviewPreviewComponents
   * and stores them in an array. This array is used to collapse other opened LimitedHtmlComponents in the list.
   *
   * @param previews list of the ReviewPreviewComponent sub components.
   */
  createPreviewArray(previews: QueryList<ReviewPreviewComponent>) {
    if (previews) {
      previews.changes.pipe(untilDestroyed(this)).subscribe(() => {
        this.limitedTextComponents = [];
        for (const previewComponent of previews) {
          this.limitedTextComponents.push(previewComponent.limitedHtmlComponent);
        }
      });
    }
  }

  /**
   * Subscribes to the reviews returned by the kyma service.
   * Review Subscription gets a bigger list of reviews.
   * Defines a list of reviews IDs for exclusion.
   * Removes the Navigation link in the header navigation.
   *
   * @param currentProduct Product the reviews are for
   * @param currentPage Page number of the reviews.
   */
  private getAllReviews(currentProduct: Product, currentPage: number) {
    this.kymaService.setReviewsData(null);
    this.reviewsSubscription.unsubscribe();
    if (this.productUtilitiesService.getProductReviewsId(currentProduct)) {
      this.allReviews$ = this.kymaService.getReviewsForProduct(currentProduct, currentPage + 1, 92);
      this.allReviews$.pipe(untilDestroyed(this)).subscribe({
        next: (reviewList) => {
          this.setReviewsForExclusionId(reviewList);
          if (!this.isListReady) {
            this.subscribeToReview(currentProduct, currentPage);
          } else {
            if (currentPage < 1) {
              currentPage = 1;
            }
            this.subscribeToReview(currentProduct, currentPage - 1);
          }
        },
        error: () => {
          this.reviews = null;
          this.changeDetector.detectChanges();
        },
      });
    }
    this.productDetailsNavigationLinkService.removeNavigationLink(this.navLink);
  }

  /**
   * When the browser navigation button is pressed this component IS NOT destroyed.
   * This function resets the reviews to prevent unexpected behavior
   */
  resetReviewsOnNavigation(): void {
    this.routeSubscription = this.route.params.pipe(untilDestroyed(this)).subscribe(() => {
      this.reviews = null;
      this.isListReady = false;
      this.changeDetector.detectChanges();
    });
  }

  triggerEddlProductView() {
    combineLatest([this.product$, this.userFacade.get()])
      .pipe(untilDestroyed(this))
      .subscribe(([product, user]: [Product, User]) => {
        const event = new EddlProductViewEvent();

        event.collectState = EddlCollectionState.START;
        event.collectToken = user?.uid ?? product?.code;

        event.products = [];
        event.products.push({
          id: this.productUtilitiesService.getProductId(product),
          name: product?.name,
          partnerName: product?.vendor?.name,
        });
        this.eventService.dispatch(event, EddlProductViewEvent);
      });
  }
}
